import { useState, useEffect, RefObject } from 'react';
// eslint-disable-next-line import/no-relative-packages
import './match-container';

// There's unfortunately no way to fetch these values from the theme, due to CSS media queries not allowing variables,
// so we have to keep this in sync with breakpoints defined in base.css!
const breakpoints = {
	xs: '0rem',
	sm: '44rem',
	md: '48rem',
	lg: '64rem',
	xl: '80rem',
	'2xl': '96rem',
	'3xl': '120rem',
	'4xl': '160rem',
	'@sidebar-sm': '110px',
	'@sidebar-lg': '230px',
};

type TailwindBreakpoints = keyof typeof breakpoints;

// Reactively match breakpoints at runtime (mobile-first) by media query
// or predefined Tailwind breakpoint name, since some things can't be solved by pure CSS.
// Supports container queries too, by polyfill since browsers didn't bother making an API for it >:c
// https://github.com/w3c/csswg-drafts/issues/6205
export const useBreakpoint = (query: TailwindBreakpoints | string, ref?: RefObject<Element>) => {
	const [matches, setMatches] = useState(false);

	useEffect(() => {
		let match: MediaQueryList;

		const queryMatch = query.match(/(@?)(min|max)?-?(.+)/);
		if (!queryMatch) {
			match = ref?.current.matchContainer(query) ?? window.matchMedia(query);
		} else {
			const breakpoint = breakpoints[`${queryMatch[1]}${queryMatch[3]}` as TailwindBreakpoints];
			const breakpointStr = queryMatch[2] === 'max' ? `(width <= ${breakpoint})` : `(width >= ${breakpoint})`;
			match = ref?.current.matchContainer(breakpointStr) ?? window.matchMedia(breakpointStr);
		}
		setMatches(match.matches);

		const handler = (event: MediaQueryListEvent) => {
			setMatches(event.matches);
		};
		match.addEventListener('change', handler);
		return () => match.removeEventListener('change', handler);
	}, [query, ref]);

	return matches;
};
